import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';
import { App, Type, AppFooter, AppFooterLink } from '@spotify-internal/encore-creator-web';
import { Faq } from '../Faq';
import styles from './index.module.scss';
import { SpotifyForPodcastersLogo } from '../../../vendor/SpotifyForPodcastersLogo';
import { SpotifyLogo } from '../../../vendor/SpotifyLogo';
import { trackEvent } from '../../../lib/Tracking';
import { selectMe } from '../../../store/selectors';
import { ReduxState, Me } from '../../../types';

type Props = {
  me?: Me;
};

type FooterLink = {
  label: string;
  href: string;
};

export class PublicHelpPage extends React.Component<Props> {
  footerLinks: FooterLink[] = [
    {
      label: 'Legal',
      href: 'https://www.spotify.com/us/legal',
    },
    {
      label: 'Privacy Policy',
      href: 'https://www.spotify.com/legal/privacy-policy/',
    },
    {
      label: 'Cookies',
      href: 'https://www.spotify.com/legal/privacy-policy/#s13',
    },
  ];

  /* istanbul ignore next */
  get authUrl() {
    if (this.props.me) return `${window.location.origin}/gateway`;

    const continueUrl = `${window.location.origin}/gateway`;
    return `https://accounts.spotify.com/login?continue=${encodeURIComponent(continueUrl)}`;
  }

  handleLoginClick /* istanbul ignore next */ = () => {
    trackEvent({ category: 'user', action: 'login' });
  };

  renderHeader = () => (
    <div className={classNames(styles.header)}>
      <div className={styles['podcasters-logo']}>
        <SpotifyForPodcastersLogo />
      </div>
      <div className={styles['header-links']}>
        <a
          href={this.authUrl}
          className={styles['header-link']}
          onClick={this.handleLoginClick}
          data-qa="login-header-button"
        >
          <Type as="p" variant={Type.body1} color="white">
            {/* istanbul ignore next */ this.props.me ? 'Your Dashboard' : 'Log In'}
          </Type>
        </a>
      </div>
    </div>
  );

  renderHero = () => (
    <div className={styles.hero}>
      <div className={styles.container}>
        {this.renderHeader()}
        <HeroText variant={Type.display1} color="white">
          FAQ
        </HeroText>
      </div>
    </div>
  );

  renderMain = () => (
    <div className={styles.container} style={{ paddingTop: '75px' }}>
      <div className={styles.faqs}>
        <Faq />
      </div>
    </div>
  );

  renderFooterLink = (link: FooterLink) => (
    <AppFooterLink key={link.label} href={link.href} target="_blank">
      {link.label}
    </AppFooterLink>
  );

  renderFooter() {
    return (
      <div className={styles.footer}>
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.logo}>
              <SpotifyLogo />
            </div>
            <div className={styles.links}>
              <Type as="p" variant={Type.xsCaps} weight={Type.bold}>
                Helpful links
              </Type>
              <Type as="p">
                <a href="https://www.spotify.com/us/about-us/contact/">About</a>
              </Type>
              <Type as="p">
                <a href="https://newsroom.spotify.com/">Press &amp; Media</a>
              </Type>
              <Type as="p">
                <a href="https://support.spotify.com/us/contact-spotify-support">Contact Us</a>
              </Type>
            </div>
          </div>
          <div className={styles['layout-footer']}>
            <ThemeProvider theme={App.darkTheme}>
              <AppFooter
                copyrightYear={new Date().getFullYear()}
                list={this.footerLinks.map(this.renderFooterLink)}
              />
            </ThemeProvider>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        <Helmet title="FAQ" />
        <div className={styles.wrapper}>
          {this.renderHero()}
          {this.renderMain()}
        </div>
        {this.renderFooter()}
      </div>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    me: selectMe(state),
  };
}

export const PublicHelpPageWithRouter = connect(mapStateToProps)(PublicHelpPage);

const HeroText = styled(Type.h1)`
  font-size: 80px !important;
  padding: 133px 0;
  line-height: 1em !important;

  @media (max-width: 480px) {
    font-size: 50px !important;
    padding: 120px 0 100px;
  }
`;
